import React, { useEffect, useState } from 'react';
import {
  useGetSettingsQuery,
  useLazyGetSuggestionCoverageQuery,
  usePostSocialNowMutation,
  useRunEventPurgeMutation,
  useRunEventSuggestionPushMutation,
  useRunReengagementPushMutation,
  useUpdateSettingsMutation,
  type AppSettings,
  type BoostPrices,
  type EventPurgeResult,
  type ReengagementRunResult,
  type SocialLimits,
  type SocialPostPlatform,
  type SuggestionCoverageResult,
  type SuggestionKind,
  type SuggestionRunResult,
  type TourSocialLink,
} from '../../redux/settings/settings.api';
import threadsIcon from '../../assets/images/threads.png';
import instaPostIcon from '../../assets/images/insta_post.png';
import instaStoryIcon from '../../assets/images/insta_story.png';

interface FormState {
  isMaintenanceMode: boolean;
  isIngestionEnabled: boolean;
  isAutoPublishEnabled: boolean;
  autoPublishPerCity: number;
  warningPopupText: string;
  requiredMinVersionIos: string;
  suggestedMinVersionIos: string;
  requiredMinVersionAndroid: string;
  suggestedMinVersionAndroid: string;
  tourSocialLinks: TourSocialLink[];
  socialLimits: SocialLimits;
  isSocialEnabled: boolean;
  isSocialAutoApprove: boolean;
  isReengagementPushEnabled: boolean;
  isEventPurgeEnabled: boolean;
  eventPurgeRetentionDays: number;
  platformFeePercent: number;
  platformFeeFixed: number;
  minPaidPrice: number;
  boostPrices: BoostPrices;
  isPaymentsEnabled: boolean;
  velocityWindowMinutes: number;
  maxFailedPayments: number;
  maxSucceededPayments: number;
  force3ds: boolean;
  payoutMinAccountAgeHours: number;
  isEventSuggestionPushEnabled: boolean;
  // Nullable on purpose — see SUGGESTION_FIELDS. `null` = "not set, use the
  // backend default", and it is what an empty input holds.
  suggestionMaxPerDay: number | null;
  suggestionMaxPerWeek: number | null;
  suggestionMinScoreExact: number | null;
  suggestionMinScoreBridge: number | null;
  suggestionMaxUsersPerEvent: number | null;
}

/**
 * The five suggestion tunables the backend keeps nullable on purpose. A NULL
 * column means "use the declared default in DEFAULT_SUGGESTION_MATCH_CONFIG",
 * never "no limit" — so the form must never write one back that the admin did
 * not actually type. Saving all five unconditionally would freeze whatever the
 * defaults happened to be on the day someone first pressed Save, and a later
 * change to the backend default would silently never reach this environment.
 */
const SUGGESTION_FIELDS = [
  'suggestionMaxPerDay',
  'suggestionMaxPerWeek',
  'suggestionMinScoreExact',
  'suggestionMinScoreBridge',
  'suggestionMaxUsersPerEvent',
] as const;

type SuggestionField = (typeof SUGGESTION_FIELDS)[number];

/**
 * Mirrors DEFAULT_SUGGESTION_MATCH_CONFIG on the backend. Rendered as the input
 * *placeholder* when the stored value is null — never as its value, so it is
 * never submitted and never materialised into the database.
 */
const DEFAULT_SUGGESTION_SETTINGS: Record<SuggestionField, number> = {
  suggestionMaxPerDay: 1,
  suggestionMaxPerWeek: 3,
  suggestionMinScoreExact: 0.3,
  suggestionMinScoreBridge: 0.42,
  suggestionMaxUsersPerEvent: 200,
};

/**
 * Empty stays empty. `Number('') || 0` is 0, and a stored 0 score floor makes
 * the matcher reject nothing — the opposite of "a weak bridge match loses to
 * sending nothing". Clamp only when there is a real number to clamp.
 */
const parseNullableNumber = (
  raw: string,
  min: number,
  max = Number.POSITIVE_INFINITY,
): number | null => {
  if (raw.trim() === '') return null;
  const parsed = Number(raw);
  if (!Number.isFinite(parsed)) return null;
  return Math.min(max, Math.max(min, parsed));
};

/**
 * Only the tunables the admin actually changed. Everything else is left out of
 * the PATCH body entirely, so an untouched NULL stays NULL and a blank input
 * sends nothing rather than 0. Clearing an already-stored value is therefore a
 * no-op — deliberately, because the alternative is writing a 0 we do not mean.
 */
const changedSuggestionSettings = (
  form: FormState,
  loaded: AppSettings | undefined,
): Partial<Record<SuggestionField, number>> => {
  const changed: Partial<Record<SuggestionField, number>> = {};
  SUGGESTION_FIELDS.forEach((key) => {
    const value = form[key];
    if (value === null) return;
    if (value === (loaded?.[key] ?? null)) return;
    changed[key] = value;
  });
  return changed;
};

/** The two passes, in the order the 17:00 cron runs them. */
const SUGGESTION_KINDS: { kind: SuggestionKind; label: string; hint: string }[] = [
  {
    kind: 'starting_soon',
    label: 'Starting soon',
    hint: 'events 24–72 h out — runs first, because it is time-critical',
  },
  {
    kind: 'new_event',
    label: 'New events',
    hint: 'published in the last 36 h, starting 12 h+ from now',
  },
];

const DEFAULT_BOOST_PRICES: BoostPrices = {
  '4': 2,
  '12': 4,
  '24': 6,
  '72': 14,
  '168': 25,
};

/** Boost tiers in display order, with a human label for each duration. */
const BOOST_TIERS: { key: keyof BoostPrices; label: string }[] = [
  { key: '4', label: '4 hours' },
  { key: '12', label: '12 hours' },
  { key: '24', label: '1 day' },
  { key: '72', label: '3 days' },
  { key: '168', label: '1 week' },
];

const DEFAULT_SOCIAL_LIMITS: SocialLimits = {
  threadsPerDay: 3,
  instagramPerDay: 1,
  storiesPerDay: 1,
  carouselEveryDays: 2,
  reelEveryDays: 3,
  tiktokEveryDays: 3,
};

const emptyForm: FormState = {
  isMaintenanceMode: false,
  isIngestionEnabled: false,
  isAutoPublishEnabled: false,
  autoPublishPerCity: 30,
  warningPopupText: '',
  requiredMinVersionIos: '',
  suggestedMinVersionIos: '',
  requiredMinVersionAndroid: '',
  suggestedMinVersionAndroid: '',
  tourSocialLinks: [],
  socialLimits: DEFAULT_SOCIAL_LIMITS,
  isSocialEnabled: false,
  isSocialAutoApprove: false,
  isReengagementPushEnabled: false,
  isEventPurgeEnabled: false,
  eventPurgeRetentionDays: 30,
  platformFeePercent: 5,
  platformFeeFixed: 0.3,
  minPaidPrice: 3,
  boostPrices: DEFAULT_BOOST_PRICES,
  isPaymentsEnabled: true,
  // Mirrors DEFAULT_FRAUD_SETTINGS on the backend.
  velocityWindowMinutes: 60,
  maxFailedPayments: 3,
  maxSucceededPayments: 5,
  force3ds: true,
  payoutMinAccountAgeHours: 24,
  isEventSuggestionPushEnabled: false,
  suggestionMaxPerDay: null,
  suggestionMaxPerWeek: null,
  suggestionMinScoreExact: null,
  suggestionMinScoreBridge: null,
  suggestionMaxUsersPerEvent: null,
};

const extractErrorMessage = (err: unknown): string => {
  if (typeof err === 'object' && err !== null) {
    const e = err as { data?: { message?: string }; status?: number | string };
    if (e.data?.message) return e.data.message;
    if (e.status === 'FETCH_ERROR') return 'Network error.';
  }
  return 'Save failed';
};

export const AdminAppSettings = (): React.JSX.Element => {
  const { data, isLoading, error } = useGetSettingsQuery();
  const [save, { isLoading: isSaving }] = useUpdateSettingsMutation();
  const [postSocialNow] = usePostSocialNowMutation();
  const [postingPlatform, setPostingPlatform] =
    useState<SocialPostPlatform | null>(null);
  const [postResult, setPostResult] = useState<string | null>(null);

  const [form, setForm] = useState<FormState>(emptyForm);
  const [savedAt, setSavedAt] = useState<Date | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (!data) return;
    setForm({
      isMaintenanceMode: data.isMaintenanceMode,
      isIngestionEnabled: data.isIngestionEnabled,
      isAutoPublishEnabled: data.isAutoPublishEnabled ?? false,
      autoPublishPerCity: data.autoPublishPerCity ?? 30,
      warningPopupText: data.warningPopupText,
      requiredMinVersionIos: data.requiredMinVersionIos,
      suggestedMinVersionIos: data.suggestedMinVersionIos,
      requiredMinVersionAndroid: data.requiredMinVersionAndroid,
      suggestedMinVersionAndroid: data.suggestedMinVersionAndroid,
      tourSocialLinks: data.tourSocialLinks ?? [],
      socialLimits: { ...DEFAULT_SOCIAL_LIMITS, ...(data.socialLimits ?? {}) },
      isSocialEnabled: data.isSocialEnabled ?? false,
      isSocialAutoApprove: data.isSocialAutoApprove ?? false,
      isReengagementPushEnabled: data.isReengagementPushEnabled ?? false,
      isEventPurgeEnabled: data.isEventPurgeEnabled ?? false,
      eventPurgeRetentionDays: data.eventPurgeRetentionDays ?? 30,
      platformFeePercent: data.platformFeePercent ?? 5,
      platformFeeFixed: data.platformFeeFixed ?? 0.3,
      minPaidPrice: data.minPaidPrice ?? 3,
      boostPrices: { ...DEFAULT_BOOST_PRICES, ...(data.boostPrices ?? {}) },
      // Absent on an older backend means payments are on, not off.
      isPaymentsEnabled: data.isPaymentsEnabled !== false,
      velocityWindowMinutes: data.velocityWindowMinutes ?? 60,
      maxFailedPayments: data.maxFailedPayments ?? 3,
      maxSucceededPayments: data.maxSucceededPayments ?? 5,
      // Absent on an older backend means the protection is on, not off.
      force3ds: data.isForce3dsEnabled !== false,
      payoutMinAccountAgeHours: data.payoutMinAccountAgeHours ?? 24,
      // Absent means OFF here — the opposite of payments and 3DS, because this
      // switch ships disarmed.
      isEventSuggestionPushEnabled: data.isEventSuggestionPushEnabled === true,
      // null stays null: the backend default is shown as the placeholder, so
      // an untouched field is never sent back and never stored.
      suggestionMaxPerDay: data.suggestionMaxPerDay ?? null,
      suggestionMaxPerWeek: data.suggestionMaxPerWeek ?? null,
      suggestionMinScoreExact: data.suggestionMinScoreExact ?? null,
      suggestionMinScoreBridge: data.suggestionMinScoreBridge ?? null,
      suggestionMaxUsersPerEvent: data.suggestionMaxUsersPerEvent ?? null,
    });
  }, [data]);

  const onSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSaveError(null);
    const turningMaintenanceOn =
      form.isMaintenanceMode && data && !data.isMaintenanceMode;
    if (turningMaintenanceOn) {
      const ok = window.confirm(
        'Turn ON maintenance mode?\n\nAll mobile users will see the warning popup and be blocked from using the app until you turn it back off.',
      );
      if (!ok) return;
    }
    const turningPaymentsOff =
      !form.isPaymentsEnabled && data && data.isPaymentsEnabled !== false;
    if (turningPaymentsOff) {
      const ok = window.confirm(
        'Stop taking payments?\n\nNo new ticket, membership, subscription or boost charge will be created until you turn this back on. Payments already in flight still confirm, and refunds keep working.',
      );
      if (!ok) return;
    }
    try {
      await save({
        isMaintenanceMode: form.isMaintenanceMode,
        isIngestionEnabled: form.isIngestionEnabled,
        isAutoPublishEnabled: form.isAutoPublishEnabled,
        autoPublishPerCity: form.autoPublishPerCity,
        warningPopupText: form.warningPopupText,
        requiredMinVersionIos: form.requiredMinVersionIos,
        suggestedMinVersionIos: form.suggestedMinVersionIos,
        requiredMinVersionAndroid: form.requiredMinVersionAndroid,
        suggestedMinVersionAndroid: form.suggestedMinVersionAndroid,
        tourSocialLinks: form.tourSocialLinks,
        socialLimits: form.socialLimits,
        isSocialEnabled: form.isSocialEnabled,
        isSocialAutoApprove: form.isSocialAutoApprove,
        isReengagementPushEnabled: form.isReengagementPushEnabled,
        isEventPurgeEnabled: form.isEventPurgeEnabled,
        eventPurgeRetentionDays: form.eventPurgeRetentionDays,
        platformFeePercent: form.platformFeePercent,
        platformFeeFixed: form.platformFeeFixed,
        minPaidPrice: form.minPaidPrice,
        boostPrices: form.boostPrices,
        isPaymentsEnabled: form.isPaymentsEnabled,
        velocityWindowMinutes: form.velocityWindowMinutes,
        maxFailedPayments: form.maxFailedPayments,
        maxSucceededPayments: form.maxSucceededPayments,
        force3ds: form.force3ds,
        payoutMinAccountAgeHours: form.payoutMinAccountAgeHours,
        isEventSuggestionPushEnabled: form.isEventSuggestionPushEnabled,
        ...changedSuggestionSettings(form, data),
      }).unwrap();
      setSavedAt(new Date());
    } catch (err) {
      setSaveError(extractErrorMessage(err));
    }
  };

  const [runReengagement, { isLoading: isRunningReengagement }] =
    useRunReengagementPushMutation();
  const [reengageResult, setReengageResult] =
    useState<ReengagementRunResult | null>(null);
  const [reengageError, setReengageError] = useState<string | null>(null);

  const triggerReengagement = async (dryRun: boolean): Promise<void> => {
    if (!dryRun) {
      const ok = window.confirm(
        'Send re-engagement pushes NOW?\n\nThis runs a real pass: every eligible inactive user gets a push notification (requires the toggle to be ON and saved).',
      );
      if (!ok) return;
    }
    setReengageError(null);
    setReengageResult(null);
    try {
      const res = await runReengagement({ dryRun }).unwrap();
      setReengageResult(res);
    } catch (err) {
      setReengageError(extractErrorMessage(err));
    }
  };

  const [runPurge, { isLoading: isRunningPurge }] = useRunEventPurgeMutation();
  const [purgeResult, setPurgeResult] = useState<EventPurgeResult | null>(null);
  const [purgeError, setPurgeError] = useState<string | null>(null);

  const triggerPurge = async (dryRun: boolean): Promise<void> => {
    if (!dryRun) {
      const ok = window.confirm(
        'Delete abandoned past events NOW?\n\nThis permanently removes ingested events older than the retention below that nobody ever joined, paid for, shared or reported. It cannot be undone.',
      );
      if (!ok) return;
    }
    setPurgeError(null);
    setPurgeResult(null);
    try {
      const res = await runPurge({ dryRun }).unwrap();
      setPurgeResult(res);
    } catch (err) {
      setPurgeError(extractErrorMessage(err));
    }
  };

  const [runSuggestions, { isLoading: isRunningSuggestions }] =
    useRunEventSuggestionPushMutation();
  const [loadCoverage, { isFetching: isLoadingCoverage }] =
    useLazyGetSuggestionCoverageQuery();
  const [suggestionResult, setSuggestionResult] =
    useState<SuggestionRunResult | null>(null);
  const [suggestionError, setSuggestionError] = useState<string | null>(null);
  const [coverage, setCoverage] = useState<SuggestionCoverageResult | null>(null);
  // Which button is busy — otherwise all four disable together and the admin
  // cannot tell which pass is running.
  const [runningKind, setRunningKind] = useState<string | null>(null);

  const triggerSuggestions = async (
    kind: SuggestionKind,
    dryRun: boolean,
  ): Promise<void> => {
    if (!dryRun) {
      const ok = window.confirm(
        `Send "${kind}" suggestion pushes NOW?\n\nThis runs a real pass: every matched user gets one push, and each (user, event) pair is burned permanently — it can never be suggested to that person again, even if delivery fails. Requires the toggle to be ON and saved.`,
      );
      if (!ok) return;
    }
    setSuggestionError(null);
    setSuggestionResult(null);
    setRunningKind(`${kind}:${dryRun}`);
    try {
      const res = await runSuggestions({ kind, dryRun }).unwrap();
      setSuggestionResult(res);
    } catch (err) {
      setSuggestionError(extractErrorMessage(err));
    } finally {
      setRunningKind(null);
    }
  };

  const triggerCoverage = async (): Promise<void> => {
    setSuggestionError(null);
    try {
      setCoverage(await loadCoverage().unwrap());
    } catch (err) {
      setSuggestionError(extractErrorMessage(err));
    }
  };

  const triggerSocialPost = async (
    platform: SocialPostPlatform,
    label: string,
  ): Promise<void> => {
    setPostResult(null);
    setPostingPlatform(platform);
    try {
      const res = await postSocialNow(platform).unwrap();
      setPostResult(
        res.status === 'draft'
          ? `${label}: draft created — approve it from the review email.`
          : `${label}: publishing now 🚀 — the result email is on its way.`,
      );
    } catch (err) {
      setPostResult(`${label}: ${extractErrorMessage(err)}`);
    } finally {
      setPostingPlatform(null);
    }
  };

  /** Blank clears the field back to "not set" instead of collapsing to 0. */
  const updateSuggestion = (
    key: SuggestionField,
    raw: string,
    min: number,
    max?: number,
  ): void => {
    setForm((prev) => ({ ...prev, [key]: parseNullableNumber(raw, min, max) }));
  };

  const updateLimit = (key: keyof SocialLimits, raw: string): void => {
    const parsed = parseInt(raw, 10);
    const value = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    setForm((prev) => ({
      ...prev,
      socialLimits: { ...prev.socialLimits, [key]: value },
    }));
  };

  const updateLink = (i: number, key: keyof TourSocialLink, value: string): void => {
    setForm((prev) => ({
      ...prev,
      tourSocialLinks: prev.tourSocialLinks.map((link, idx) =>
        idx === i ? { ...link, [key]: value } : link,
      ),
    }));
  };

  const addLink = (): void => {
    setForm((prev) => ({
      ...prev,
      tourSocialLinks: [
        ...prev.tourSocialLinks,
        { icon: '', label: '', url: '' },
      ],
    }));
  };

  const removeLink = (i: number): void => {
    setForm((prev) => ({
      ...prev,
      tourSocialLinks: prev.tourSocialLinks.filter((_, idx) => idx !== i),
    }));
  };

  if (isLoading && !data) {
    return <div className="admin-users__loading">Loading settings…</div>;
  }

  if (error) {
    return (
      <div className="admin-users__error">
        Failed to load settings. Try again.
      </div>
    );
  }

  return (
    <form className="app-settings" onSubmit={onSubmit}>
      <div className="admin-users__header">
        <div className="admin-users__title">
          <h2>App settings</h2>
        </div>
        <div className="app-settings__save-row">
          {savedAt && (
            <span className="app-settings__saved-at">
              Saved {savedAt.toLocaleTimeString()}
            </span>
          )}
          <button
            type="submit"
            className="btn btn-primary app-settings__save-btn"
            disabled={isSaving}
          >
            {isSaving ? 'Saving…' : 'Save changes'}
          </button>
        </div>
      </div>

      {saveError && (
        <div className="admin-users__error">{saveError}</div>
      )}

      <section className="user-detail__card user-detail__card--wide">
        <h3 className="user-detail__card-title">Maintenance</h3>
        <label className="app-settings__toggle">
          <input
            type="checkbox"
            checked={form.isMaintenanceMode}
            onChange={(e) =>
              setForm({ ...form, isMaintenanceMode: e.target.checked })
            }
          />
          <span className="app-settings__toggle-track">
            <span className="app-settings__toggle-knob" />
          </span>
          <span className="app-settings__toggle-text">
            Maintenance mode
            {form.isMaintenanceMode && (
              <span className="app-settings__warning-pill">
                MOBILE APP BLOCKED
              </span>
            )}
          </span>
        </label>
        {form.isMaintenanceMode && (
          <p className="app-settings__danger-note">
            When ON, the mobile app will show the warning text below to all
            users and block normal functionality.
          </p>
        )}
        <label className="app-settings__field">
          <span className="app-settings__field-label">Warning popup text</span>
          <textarea
            className="app-settings__textarea"
            rows={3}
            value={form.warningPopupText}
            onChange={(e) =>
              setForm({ ...form, warningPopupText: e.target.value })
            }
            placeholder="We are currently fixing some issues. Please check back later!"
          />
        </label>
      </section>

      <section className="user-detail__card user-detail__card--wide">
        <h3 className="user-detail__card-title">Event ingestion</h3>
        <label className="app-settings__toggle">
          <input
            type="checkbox"
            checked={form.isIngestionEnabled}
            onChange={(e) =>
              setForm({ ...form, isIngestionEnabled: e.target.checked })
            }
          />
          <span className="app-settings__toggle-track">
            <span className="app-settings__toggle-knob" />
          </span>
          <span className="app-settings__toggle-text">
            Daily auto-discovery{' '}
            {form.isIngestionEnabled ? '· runs 04:00 daily' : '· off'}
          </span>
        </label>
        <p className="app-settings__hint">
          When ON, the backend scans the configured event sources every day at
          04:00 and adds new events to the <strong>Candidates</strong> queue as{' '}
          <em>Pending</em>. Nothing goes live until you approve it. The manual{' '}
          <strong>Run ingestion</strong> button works regardless of this toggle.
        </p>

        <label className="app-settings__toggle">
          <input
            type="checkbox"
            checked={form.isAutoPublishEnabled}
            onChange={(e) =>
              setForm({ ...form, isAutoPublishEnabled: e.target.checked })
            }
          />
          <span className="app-settings__toggle-track">
            <span className="app-settings__toggle-knob" />
          </span>
          <span className="app-settings__toggle-text">
            Auto-publish best candidates{' '}
            {form.isAutoPublishEnabled ? '· runs 05:00 daily' : '· off'}
          </span>
        </label>
        <div className="app-settings__grid">
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Max auto-published per city per run
            </span>
            <input
              type="number"
              min={1}
              max={200}
              className="app-settings__input"
              value={form.autoPublishPerCity}
              onChange={(e) =>
                setForm({
                  ...form,
                  autoPublishPerCity: Math.max(1, Number(e.target.value) || 30),
                })
              }
            />
          </label>
        </div>
        <p className="app-settings__hint">
          When ON, an hour after ingestion the backend automatically approves
          up to <strong>{form.autoPublishPerCity}</strong> pending candidates
          per city that pass the strict quality gate (name, date, city,{' '}
          <strong>image and a description of 80+ chars</strong>). The cap is
          spread across upcoming days (each day&apos;s best-ranked candidate
          first), so the feed fills weeks ahead instead of only the next day.
          Everything else stays <em>Pending</em> for manual review — nothing is
          auto-rejected.
        </p>
      </section>

      <section className="user-detail__card user-detail__card--wide">
        <h3 className="user-detail__card-title">Event cleanup</h3>
        <label className="app-settings__toggle">
          <input
            type="checkbox"
            checked={form.isEventPurgeEnabled}
            onChange={(e) =>
              setForm({ ...form, isEventPurgeEnabled: e.target.checked })
            }
          />
          <span className="app-settings__toggle-track">
            <span className="app-settings__toggle-knob" />
          </span>
          <span className="app-settings__toggle-text">
            Purge abandoned past events{' '}
            {form.isEventPurgeEnabled ? '· runs 03:30 daily' : '· off'}
          </span>
        </label>
        <div className="app-settings__grid">
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Keep finished events for (days)
            </span>
            <input
              type="number"
              min={1}
              max={3650}
              className="app-settings__input"
              value={form.eventPurgeRetentionDays}
              onChange={(e) =>
                setForm({
                  ...form,
                  eventPurgeRetentionDays: Math.max(
                    1,
                    Number(e.target.value) || 30,
                  ),
                })
              }
            />
          </label>
        </div>
        <p className="app-settings__hint">
          When ON, at 03:30 the backend deletes <strong>ingested</strong> events
          that ended more than{' '}
          <strong>{form.eventPurgeRetentionDays} days</strong> ago and that
          nobody ever touched. An event is kept if it has a single application,
          pending application, waitlist entry, payment, boost, report or
          attachment, if it was posted to social, or if somebody shared it in a
          post or a chat. Events created by users are never deleted, whatever
          their age. Stale notifications pointing at a deleted event go with it,
          so nobody is left with a dead link.
        </p>
        <div className="app-settings__social-btns">
          <button
            type="button"
            className="app-settings__social-btn"
            disabled={isRunningPurge}
            onClick={() => triggerPurge(true)}
          >
            {isRunningPurge ? 'Running…' : 'Preview (dry run)'}
          </button>
          <button
            type="button"
            className="app-settings__social-btn"
            disabled={isRunningPurge}
            onClick={() => triggerPurge(false)}
          >
            Purge now
          </button>
        </div>
        <p className="app-settings__hint">
          <strong>Preview</strong> counts what would go without deleting
          anything (works even while the toggle is off).{' '}
          <strong>Purge now</strong> deletes immediately and ignores the toggle
          — the toggle only decides whether the nightly cron runs. Deletion is
          permanent.
        </p>
        {purgeError && (
          <p className="app-settings__hint">
            <strong>{purgeError}</strong>
          </p>
        )}
        {purgeResult && (
          <>
            <p className="app-settings__hint">
              <strong>
                {purgeResult.dryRun
                  ? `Dry run: ${purgeResult.eventsDeleted} events and ${purgeResult.notificationsDeleted} stale notifications would be deleted.`
                  : `Done: ${purgeResult.eventsDeleted} events and ${purgeResult.notificationsDeleted} notifications deleted in ${purgeResult.batches} batches (${purgeResult.durationMs} ms).`}
              </strong>
            </p>
            {purgeResult.errors.length > 0 && (
              <p className="app-settings__hint">
                <strong>Stopped early:</strong> {purgeResult.errors.join(' · ')}
              </p>
            )}
            {purgeResult.sample.length > 0 && (
              <ul className="app-settings__hint">
                {purgeResult.sample.slice(0, 8).map((event) => (
                  <li key={event.id}>
                    {event.name}{' '}
                    <em>
                      ({event.source}, ended{' '}
                      {new Date(event.endDate).toLocaleDateString()})
                    </em>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      <section className="user-detail__card user-detail__card--wide">
        <h3 className="user-detail__card-title">Social agent</h3>
        <label className="app-settings__toggle">
          <input
            type="checkbox"
            checked={form.isSocialEnabled}
            onChange={(e) =>
              setForm({ ...form, isSocialEnabled: e.target.checked })
            }
          />
          <span className="app-settings__toggle-track">
            <span className="app-settings__toggle-knob" />
          </span>
          <span className="app-settings__toggle-text">
            Auto-posting agent{' '}
            {form.isSocialEnabled ? '· slots 09:00 / 14:00 / 19:00' : '· off'}
          </span>
        </label>
        <label className="app-settings__toggle">
          <input
            type="checkbox"
            checked={form.isSocialAutoApprove}
            onChange={(e) =>
              setForm({ ...form, isSocialAutoApprove: e.target.checked })
            }
          />
          <span className="app-settings__toggle-track">
            <span className="app-settings__toggle-knob" />
          </span>
          <span className="app-settings__toggle-text">
            Auto-approve
            {form.isSocialAutoApprove && (
              <span className="app-settings__warning-pill">
                PUBLISHES WITHOUT REVIEW
              </span>
            )}
          </span>
        </label>
        <p className="app-settings__hint">
          When <strong>Auto-approve</strong> is off, every generated post waits
          for your approval in the review email. Daily caps below are applied
          on the next generation slot — no restart needed. Set 0 to disable a
          platform.
        </p>
        <div className="app-settings__social-btns">
          <button
            type="button"
            className="app-settings__social-btn app-settings__social-btn--story"
            disabled={postingPlatform !== null}
            onClick={() => triggerSocialPost('instagram_story', 'Story')}
          >
            <img src={instaStoryIcon} alt="" />
            {postingPlatform === 'instagram_story' ? 'Posting…' : 'Post Story'}
          </button>
          <button
            type="button"
            className="app-settings__social-btn app-settings__social-btn--instagram"
            disabled={postingPlatform !== null}
            onClick={() => triggerSocialPost('instagram', 'Instagram post')}
          >
            <img src={instaPostIcon} alt="" />
            {postingPlatform === 'instagram' ? 'Posting…' : 'Post Instagram Post'}
          </button>
          <button
            type="button"
            className="app-settings__social-btn app-settings__social-btn--carousel"
            disabled={postingPlatform !== null}
            onClick={() => triggerSocialPost('instagram_carousel', 'Carousel')}
          >
            <img src={instaPostIcon} alt="" />
            {postingPlatform === 'instagram_carousel' ? 'Posting…' : 'Post Carousel'}
          </button>
          <button
            type="button"
            className="app-settings__social-btn app-settings__social-btn--reel"
            disabled={postingPlatform !== null}
            onClick={() => triggerSocialPost('instagram_reel', 'Reel')}
          >
            <img src={instaStoryIcon} alt="" />
            {postingPlatform === 'instagram_reel' ? 'Posting…' : 'Post Reel'}
          </button>
          <button
            type="button"
            className="app-settings__social-btn app-settings__social-btn--tiktok"
            disabled={postingPlatform !== null}
            onClick={() => triggerSocialPost('tiktok', 'TikTok')}
          >
            {postingPlatform === 'tiktok' ? 'Posting…' : 'Post TikTok'}
          </button>
          <button
            type="button"
            className="app-settings__social-btn app-settings__social-btn--threads"
            disabled={postingPlatform !== null}
            onClick={() => triggerSocialPost('threads', 'Threads post')}
          >
            <img src={threadsIcon} alt="" />
            {postingPlatform === 'threads' ? 'Posting…' : 'Post Threads Post'}
          </button>
        </div>
        <p className="app-settings__hint">
          The buttons generate and post right away (drafts first if
          auto-approve is off). Manual posts ignore the daily caps.
        </p>
        {postResult && <p className="app-settings__hint"><strong>{postResult}</strong></p>}
        <div className="app-settings__grid">
          <label className="app-settings__field">
            <span className="app-settings__field-label">Threads posts / day</span>
            <input
              type="number"
              min={0}
              className="app-settings__input"
              value={form.socialLimits.threadsPerDay}
              onChange={(e) => updateLimit('threadsPerDay', e.target.value)}
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">Instagram posts / day</span>
            <input
              type="number"
              min={0}
              className="app-settings__input"
              value={form.socialLimits.instagramPerDay}
              onChange={(e) => updateLimit('instagramPerDay', e.target.value)}
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">Stories / day</span>
            <input
              type="number"
              min={0}
              className="app-settings__input"
              value={form.socialLimits.storiesPerDay}
              onChange={(e) => updateLimit('storiesPerDay', e.target.value)}
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Carousel every N days (0 = off)
            </span>
            <input
              type="number"
              min={0}
              className="app-settings__input"
              value={form.socialLimits.carouselEveryDays}
              onChange={(e) => updateLimit('carouselEveryDays', e.target.value)}
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Reel every N days (0 = off)
            </span>
            <input
              type="number"
              min={0}
              className="app-settings__input"
              value={form.socialLimits.reelEveryDays}
              onChange={(e) => updateLimit('reelEveryDays', e.target.value)}
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              TikTok every N days (0 = off)
            </span>
            <input
              type="number"
              min={0}
              className="app-settings__input"
              value={form.socialLimits.tiktokEveryDays}
              onChange={(e) => updateLimit('tiktokEveryDays', e.target.value)}
            />
          </label>
        </div>
      </section>

      <section className="user-detail__card user-detail__card--wide">
        <h3 className="user-detail__card-title">Re-engagement pushes</h3>
        <label className="app-settings__toggle">
          <input
            type="checkbox"
            checked={form.isReengagementPushEnabled}
            onChange={(e) =>
              setForm({ ...form, isReengagementPushEnabled: e.target.checked })
            }
          />
          <span className="app-settings__toggle-track">
            <span className="app-settings__toggle-knob" />
          </span>
          <span className="app-settings__toggle-text">
            Daily re-engagement pushes{' '}
            {form.isReengagementPushEnabled ? '· runs 18:00 daily' : '· off'}
          </span>
        </label>
        <p className="app-settings__hint">
          When ON, at 18:00 the backend sends a localized “come back” /
          “new events in your city” push to users who haven&apos;t opened the
          app for <strong>3+ days</strong> (max one push per user every 3 days,
          stops after 60 days of inactivity). Texts rotate randomly and follow
          each user&apos;s app language. The toggle is per environment (dev and
          prod have separate databases) — enable it only where intended.
        </p>
        <div className="app-settings__social-btns">
          <button
            type="button"
            className="app-settings__social-btn"
            disabled={isRunningReengagement}
            onClick={() => triggerReengagement(true)}
          >
            {isRunningReengagement ? 'Running…' : 'Preview (dry run)'}
          </button>
          <button
            type="button"
            className="app-settings__social-btn"
            disabled={isRunningReengagement}
            onClick={() => triggerReengagement(false)}
          >
            Send now
          </button>
        </div>
        <p className="app-settings__hint">
          <strong>Preview</strong> composes the messages without sending
          anything (works even while the toggle is off).{' '}
          <strong>Send now</strong> runs a real pass and respects the saved
          toggle.
        </p>
        {reengageError && (
          <p className="app-settings__hint"><strong>{reengageError}</strong></p>
        )}
        {reengageResult && (
          <>
            <p className="app-settings__hint">
              <strong>
                {!reengageResult.enabled
                  ? 'Nothing sent — the toggle is OFF (save it as ON first).'
                  : reengageResult.dryRun
                    ? `Dry run: ${reengageResult.eligibleUsers} eligible users, ${reengageResult.sent} would receive a push.`
                    : `Done: ${reengageResult.sent} sent, ${reengageResult.failed} failed of ${reengageResult.eligibleUsers} eligible.`}
              </strong>
            </p>
            {reengageResult.sample.length > 0 && (
              <ul className="app-settings__hint">
                {reengageResult.sample.slice(0, 8).map((s) => (
                  <li key={s.userId}>
                    <strong>{s.title}</strong> — {s.body}{' '}
                    <em>
                      ({s.language ?? 'en'}
                      {s.city ? `, ${s.city}` : ''}
                      {s.newEventsCount > 0 ? `, ${s.newEventsCount} new events` : ''})
                    </em>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </section>

      <section className="user-detail__card user-detail__card--wide">
        <h3 className="user-detail__card-title">
          Interest-based event suggestions
        </h3>
        <label className="app-settings__toggle">
          <input
            type="checkbox"
            checked={form.isEventSuggestionPushEnabled}
            onChange={(e) =>
              setForm({
                ...form,
                isEventSuggestionPushEnabled: e.target.checked,
              })
            }
          />
          <span className="app-settings__toggle-track">
            <span className="app-settings__toggle-knob" />
          </span>
          <span className="app-settings__toggle-text">
            Daily suggestion pushes{' '}
            {form.isEventSuggestionPushEnabled
              ? '· runs 17:00 daily'
              : '· off'}
          </span>
        </label>
        <p className="app-settings__hint">
          When ON, at <strong>17:00</strong> (one hour before the re-engagement
          cron, so the two never overlap) the backend matches each user&apos;s
          picked interests against upcoming events in their city and sends{' '}
          <strong>one</strong> push. Two passes run in order:{' '}
          <strong>starting soon</strong> first, then <strong>new events</strong>
          {' '}— so a user who matches an imminent event will not also get a
          new-event suggestion that day. The push is just the event: title = the
          event name, body = when and where, in the user&apos;s app language.
        </p>
        <p className="app-settings__hint">
          Two hard guarantees. A <strong>(user, event) pair is suggested at
          most once, ever</strong> — the pair is claimed in the database before
          the push goes out, and it stays claimed even if delivery fails, so a
          dead token can never turn into a daily retry. And nobody who turned
          the category off in the app can be reached, because the opt-out is
          checked immediately before the send. The toggle is per environment
          (dev and prod have separate databases).
        </p>

        <div className="app-settings__grid">
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Max pushes per user per day
            </span>
            <input
              type="number"
              min={1}
              step={1}
              className="app-settings__input"
              value={form.suggestionMaxPerDay ?? ''}
              placeholder={String(DEFAULT_SUGGESTION_SETTINGS.suggestionMaxPerDay)}
              onChange={(e) =>
                updateSuggestion('suggestionMaxPerDay', e.target.value, 1)
              }
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Max pushes per user per 7 days
            </span>
            <input
              type="number"
              min={1}
              step={1}
              className="app-settings__input"
              value={form.suggestionMaxPerWeek ?? ''}
              placeholder={String(DEFAULT_SUGGESTION_SETTINGS.suggestionMaxPerWeek)}
              onChange={(e) =>
                updateSuggestion('suggestionMaxPerWeek', e.target.value, 1)
              }
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Max users per event per run (fan-out cap)
            </span>
            <input
              type="number"
              min={1}
              step={1}
              className="app-settings__input"
              value={form.suggestionMaxUsersPerEvent ?? ''}
              placeholder={String(
                DEFAULT_SUGGESTION_SETTINGS.suggestionMaxUsersPerEvent,
              )}
              onChange={(e) =>
                updateSuggestion('suggestionMaxUsersPerEvent', e.target.value, 1)
              }
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Min score — exact match (0–1)
            </span>
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              className="app-settings__input"
              value={form.suggestionMinScoreExact ?? ''}
              placeholder={String(
                DEFAULT_SUGGESTION_SETTINGS.suggestionMinScoreExact,
              )}
              onChange={(e) =>
                updateSuggestion('suggestionMinScoreExact', e.target.value, 0, 1)
              }
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Min score — bridged match (0–1)
            </span>
            <input
              type="number"
              min={0}
              max={1}
              step={0.01}
              className="app-settings__input"
              value={form.suggestionMinScoreBridge ?? ''}
              placeholder={String(
                DEFAULT_SUGGESTION_SETTINGS.suggestionMinScoreBridge,
              )}
              onChange={(e) =>
                updateSuggestion('suggestionMinScoreBridge', e.target.value, 0, 1)
              }
            />
          </label>
        </div>
        <p className="app-settings__hint">
          <strong>Leave a field blank to use the backend default</strong> — the
          greyed-out number in the box. All five are stored as NULL until an
          admin types something, and a blank field is not sent at all, so the
          environment keeps following the backend default even after it changes.
          An empty score floor is <strong>not</strong> a floor of 0: 0 would
          make the matcher reject nothing and ship the weakest bridged matches
          in the catalogue. Clearing a value that is already saved does not
          reset it back to NULL — type the default in explicitly, or clear the
          column in the database.
        </p>
        <p className="app-settings__hint">
          A <strong>0 cap does not mean unlimited</strong> — the backend clamps
          all three caps to at least 1. To stop suggestions entirely, turn the
          toggle off. The bridged floor is deliberately higher than the exact
          one: a weak category match should lose to sending nothing at all.
        </p>

        {SUGGESTION_KINDS.map(({ kind, label, hint }) => (
          <div key={kind}>
            <p className="app-settings__hint">
              <strong>{label}</strong> — {hint}
            </p>
            <div className="app-settings__social-btns">
              <button
                type="button"
                className="app-settings__social-btn"
                disabled={isRunningSuggestions}
                onClick={() => triggerSuggestions(kind, true)}
              >
                {runningKind === `${kind}:true`
                  ? 'Running…'
                  : 'Preview (dry run)'}
              </button>
              <button
                type="button"
                className="app-settings__social-btn"
                disabled={isRunningSuggestions}
                onClick={() => triggerSuggestions(kind, false)}
              >
                {runningKind === `${kind}:false` ? 'Sending…' : 'Send now'}
              </button>
            </div>
          </div>
        ))}
        <p className="app-settings__hint">
          <strong>Preview</strong> composes the messages and writes nothing at
          all — no log rows, no stamps — and works even while the toggle is off.{' '}
          <strong>Send now</strong> runs a real pass and respects the saved
          toggle.
        </p>

        <div className="app-settings__social-btns">
          <button
            type="button"
            className="app-settings__social-btn"
            disabled={isLoadingCoverage}
            onClick={triggerCoverage}
          >
            {isLoadingCoverage ? 'Loading…' : 'Check coverage'}
          </button>
        </div>

        {suggestionError && (
          <p className="app-settings__hint">
            <strong>{suggestionError}</strong>
          </p>
        )}

        {suggestionResult && (
          <>
            <p className="app-settings__hint">
              <strong>
                {!suggestionResult.enabled
                  ? 'Nothing sent — the toggle is OFF (save it as ON first).'
                  : suggestionResult.dryRun
                    ? `Dry run (${suggestionResult.kind}): ${suggestionResult.eligibleUsers} eligible users, ${suggestionResult.sent} would receive a push.`
                    : `Done (${suggestionResult.kind}): ${suggestionResult.sent} sent, ${suggestionResult.failed} failed of ${suggestionResult.eligibleUsers} eligible.`}
              </strong>
            </p>
            {/* The one number that decides whether the category bridge is
                pulling its weight or just adding noise. */}
            <p className="app-settings__hint">
              <strong>
                Exact matches: {suggestionResult.matchedExact} · Bridged
                matches: {suggestionResult.matchedBridge}
              </strong>{' '}
              — “exact” means a shared activity id, “bridged” means the
              event&apos;s category overlapped the user&apos;s interests. A
              bridge-heavy run with poor engagement is the signal to raise the
              bridged score floor above.
            </p>
            <p className="app-settings__hint">
              Skipped: {suggestionResult.skippedNoCity} no city ·{' '}
              {suggestionResult.skippedDailyCap} daily cap ·{' '}
              {suggestionResult.skippedWeeklyCap} weekly cap ·{' '}
              {suggestionResult.skippedOverBroad} interests too broad ·{' '}
              {suggestionResult.skippedNoMatch} no match ·{' '}
              {suggestionResult.skippedFanoutCap} fan-out cap.
            </p>
            <p className="app-settings__hint">
              Candidate pool:{' '}
              {Object.entries(suggestionResult.candidatesByCity).length === 0
                ? 'no cities with eligible users'
                : Object.entries(suggestionResult.candidatesByCity)
                    .map(([city, count]) => `${city}: ${count}`)
                    .join(' · ')}
            </p>
            {suggestionResult.sample.length > 0 && (
              <ul className="app-settings__hint">
                {suggestionResult.sample.slice(0, 8).map((sampleItem) => (
                  <li key={`${sampleItem.userId}-${sampleItem.eventId}`}>
                    <strong>{sampleItem.title}</strong> — {sampleItem.body}{' '}
                    <em>
                      ({sampleItem.language ?? 'en'}
                      {sampleItem.city ? `, ${sampleItem.city}` : ''},{' '}
                      {sampleItem.matchConfidence}, score{' '}
                      {sampleItem.score.toFixed(2)}, {sampleItem.key})
                    </em>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {coverage && (
          <>
            <p className="app-settings__hint">
              <strong>Coverage</strong> ({coverage.timezone}):{' '}
              {coverage.eligibleUsers} push-eligible users,{' '}
              {coverage.usersWithInterests} of them have picked interests,{' '}
              {coverage.usersWithoutCity} have no usable city (those can never
              be matched).
            </p>
            {coverage.cities.length === 0 ? (
              <p className="app-settings__hint">
                No city has any candidate event right now.
              </p>
            ) : (
              <ul className="app-settings__hint">
                {coverage.cities.slice(0, 12).map((city) => (
                  <li key={city.city}>
                    <strong>{city.city}</strong> — new: {city.newEventPool} ·
                    soon: {city.startingSoonPool} · with activities:{' '}
                    {city.withActivities} · category only: {city.categoryOnly}
                  </li>
                ))}
              </ul>
            )}
            <p className="app-settings__hint">
              “Category only” events can only ever be matched through the
              bridge, never exactly — that ratio is the ceiling on how good
              matching can get without organizers tagging activities.
            </p>
          </>
        )}
      </section>

      <section className="user-detail__card user-detail__card--wide">
        <h3 className="user-detail__card-title">Payments</h3>

        <label className="app-settings__toggle">
          <input
            type="checkbox"
            checked={form.isPaymentsEnabled}
            onChange={(e) =>
              setForm({ ...form, isPaymentsEnabled: e.target.checked })
            }
          />
          <span className="app-settings__toggle-track">
            <span className="app-settings__toggle-knob" />
          </span>
          <span className="app-settings__toggle-text">
            Accept payments
          </span>
        </label>
        <p className="app-settings__hint">
          The kill switch. Turn it OFF and the backend refuses to create any new
          charge — tickets, club memberships, subscriptions and boosts all stop
          at once, and the app hides the pay buttons. Everything needed to
          settle money already taken keeps working on purpose: buyer-side
          confirms, refunds, subscription cancel/resume and Stripe webhooks.
          Otherwise a payment captured seconds earlier would be left without a
          ticket. Takes effect immediately, no deploy.
        </p>

        <p className="app-settings__hint">
          Platform application fee on paid events &amp; clubs, taken via Stripe
          destination charges. The fee must at least cover Stripe&apos;s
          processing cost (~1.5% + a fixed ~€0.25 per transaction), otherwise
          the platform pays that cost out of pocket. The fee is{' '}
          <strong>percent × amount + fixed</strong>; the fixed part is what
          covers Stripe&apos;s fixed cost even on small amounts.
        </p>
        <div className="app-settings__grid">
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Application fee % (e.g. 5 = 5%)
            </span>
            <input
              type="number"
              min={0}
              step={0.1}
              className="app-settings__input"
              value={form.platformFeePercent}
              onChange={(e) =>
                setForm({
                  ...form,
                  platformFeePercent: Math.max(0, Number(e.target.value) || 0),
                })
              }
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Fixed fee (major units, e.g. 0.30)
            </span>
            <input
              type="number"
              min={0}
              step={0.05}
              className="app-settings__input"
              value={form.platformFeeFixed}
              onChange={(e) =>
                setForm({
                  ...form,
                  platformFeeFixed: Math.max(0, Number(e.target.value) || 0),
                })
              }
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Minimum paid price (major units)
            </span>
            <input
              type="number"
              min={0}
              step={0.5}
              className="app-settings__input"
              value={form.minPaidPrice}
              onChange={(e) =>
                setForm({
                  ...form,
                  minPaidPrice: Math.max(0, Number(e.target.value) || 0),
                })
              }
            />
          </label>
        </div>
        <p className="app-settings__hint">
          Example at {form.platformFeePercent}% + {form.platformFeeFixed}: a{' '}
          <strong>10.00</strong> ticket → fee{' '}
          <strong>
            {(10 * (form.platformFeePercent / 100) + form.platformFeeFixed).toFixed(2)}
          </strong>
          , organizer receives the rest minus Stripe&apos;s cost. Below the
          minimum price, fixed fees make the sale uneconomical — paid events and
          memberships are blocked under it. Subscriptions only support a
          percentage fee (Stripe limitation), so the minimum price keeps them
          viable.
        </p>
      </section>

      <section className="user-detail__card user-detail__card--wide">
        <h3 className="user-detail__card-title">Fraud protection</h3>
        <p className="app-settings__hint">
          Sized from the <strong>10–11 August 2026</strong> card-testing attack:
          37 throwaway accounts made <strong>521 payment attempts</strong> and
          landed <strong>183</strong> charges on stolen cards, all routed to six
          connected accounts created minutes beforehand. Loosen these if they
          ever catch a real buyer — the change takes effect immediately, no
          deploy.
        </p>

        <label className="app-settings__toggle">
          <input
            type="checkbox"
            checked={form.force3ds}
            onChange={(e) => setForm({ ...form, force3ds: e.target.checked })}
          />
          <span className="app-settings__toggle-track">
            <span className="app-settings__toggle-knob" />
          </span>
          <span className="app-settings__toggle-text">
            Force 3D Secure on every card payment
          </span>
        </label>
        <p className="app-settings__hint">
          The single most valuable control here. Once a charge is
          3DS-authenticated, liability for a &quot;cardholder did not
          authorise&quot; dispute moves to the <strong>issuing bank</strong> —
          the €20 dispute fees stop being ours. Costs a little conversion,
          because some buyers see an extra bank prompt.
        </p>

        <div className="app-settings__grid">
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Window for the two limits below (minutes)
            </span>
            {/* step must divide the values we actually use: with step=5 and
                min=1 the browser rejects 60 as a step mismatch and silently
                blocks the whole form from submitting. */}
            <input
              type="number"
              min={1}
              step={1}
              className="app-settings__input"
              value={form.velocityWindowMinutes}
              onChange={(e) =>
                setForm({
                  ...form,
                  velocityWindowMinutes: Math.max(1, Number(e.target.value) || 1),
                })
              }
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Max failed payments per user (0 = off)
            </span>
            <input
              type="number"
              min={0}
              step={1}
              className="app-settings__input"
              value={form.maxFailedPayments}
              onChange={(e) =>
                setForm({
                  ...form,
                  maxFailedPayments: Math.max(0, Number(e.target.value) || 0),
                })
              }
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Max successful payments per user (0 = off)
            </span>
            <input
              type="number"
              min={0}
              step={1}
              className="app-settings__input"
              value={form.maxSucceededPayments}
              onChange={(e) =>
                setForm({
                  ...form,
                  maxSucceededPayments: Math.max(0, Number(e.target.value) || 0),
                })
              }
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">
              Account age before Stripe onboarding (hours, 0 = off)
            </span>
            <input
              type="number"
              min={0}
              step={1}
              className="app-settings__input"
              value={form.payoutMinAccountAgeHours}
              onChange={(e) =>
                setForm({
                  ...form,
                  payoutMinAccountAgeHours: Math.max(
                    0,
                    Number(e.target.value) || 0,
                  ),
                })
              }
            />
          </label>
        </div>
        <p className="app-settings__hint">
          <strong>Failed payments</strong> is the card-stuffing signal: one
          attacker made 52 attempts to land 6 charges, while across all 37 the
          median was a single failure before the first success — so a buyer who
          mistypes a card is nowhere near the limit. A ceiling of 3 would have
          cut 251 of those 521 attempts.{' '}
          <strong>Successful payments</strong> caps whoever still gets through;
          each attacker bought 5–6 tickets. Counted across tickets, club
          memberships and boosts together, otherwise the abuse just moves one
          endpoint over.{' '}
          <strong>Account age</strong> is the structural one: all six fake
          organizers went from signup to collecting money within the hour, and
          without a connected account there is nowhere to route a payment. Real
          organizers can still create free events immediately.
        </p>
      </section>

      <section className="user-detail__card user-detail__card--wide">
        <h3 className="user-detail__card-title">Event boost prices</h3>
        <p className="app-settings__hint">
          What an organizer pays to promote an event, per duration. Prices are
          in <strong>EUR</strong> and converted into the event&apos;s own
          currency at checkout (rounded up). Boosted events appear in a slider
          above the Home feed, within the viewer&apos;s normal radius — a boost
          buys position, not reach. Paid to the platform directly, so Stripe
          Connect isn&apos;t involved and the organizer keeps 100% of ticket
          sales.
        </p>
        <div className="app-settings__grid">
          {BOOST_TIERS.map(({ key, label }) => (
            <label className="app-settings__field" key={key}>
              <span className="app-settings__field-label">{label} (EUR)</span>
              <input
                type="number"
                min={0}
                step={0.5}
                className="app-settings__input"
                value={form.boostPrices[key]}
                onChange={(e) =>
                  setForm({
                    ...form,
                    boostPrices: {
                      ...form.boostPrices,
                      [key]: Math.max(0, Number(e.target.value) || 0),
                    },
                  })
                }
              />
            </label>
          ))}
        </div>
        <p className="app-settings__hint">
          Keep each tier above ~1 EUR: Stripe&apos;s fixed per-transaction cost
          (~0.25 EUR) would otherwise eat most of the sale.
        </p>
      </section>

      <section className="user-detail__card user-detail__card--wide">
        <h3 className="user-detail__card-title">Minimum app versions</h3>
        <p className="app-settings__hint">
          <strong>Required</strong> — users below this version are forced to
          update.
          <br />
          <strong>Suggested</strong> — soft prompt to update.
        </p>
        <div className="app-settings__grid">
          <label className="app-settings__field">
            <span className="app-settings__field-label">iOS required</span>
            <input
              type="text"
              className="app-settings__input"
              placeholder="e.g. 1.2.0"
              value={form.requiredMinVersionIos}
              onChange={(e) =>
                setForm({ ...form, requiredMinVersionIos: e.target.value })
              }
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">iOS suggested</span>
            <input
              type="text"
              className="app-settings__input"
              placeholder="e.g. 1.2.0"
              value={form.suggestedMinVersionIos}
              onChange={(e) =>
                setForm({ ...form, suggestedMinVersionIos: e.target.value })
              }
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">Android required</span>
            <input
              type="text"
              className="app-settings__input"
              placeholder="e.g. 1.2.0"
              value={form.requiredMinVersionAndroid}
              onChange={(e) =>
                setForm({ ...form, requiredMinVersionAndroid: e.target.value })
              }
            />
          </label>
          <label className="app-settings__field">
            <span className="app-settings__field-label">Android suggested</span>
            <input
              type="text"
              className="app-settings__input"
              placeholder="e.g. 1.2.0"
              value={form.suggestedMinVersionAndroid}
              onChange={(e) =>
                setForm({
                  ...form,
                  suggestedMinVersionAndroid: e.target.value,
                })
              }
            />
          </label>
        </div>
      </section>

      <section className="user-detail__card user-detail__card--wide">
        <div className="event-detail__section-head">
          <h3 className="user-detail__card-title" style={{ margin: 0 }}>
            Tour social links
          </h3>
          <button
            type="button"
            className="event-detail__add-btn"
            onClick={addLink}
          >
            + Add link
          </button>
        </div>
        <p className="app-settings__hint">
          Shown in the in-app onboarding tour. Icon names are Ionicons keys
          (e.g. <code>logo-instagram</code>, <code>logo-twitter</code>).
        </p>
        {form.tourSocialLinks.length === 0 ? (
          <p className="user-detail__empty">No social links configured.</p>
        ) : (
          <div className="app-settings__links">
            {form.tourSocialLinks.map((link, i) => (
              <div key={i} className="app-settings__link-row">
                <input
                  type="text"
                  className="app-settings__input"
                  placeholder="icon (e.g. logo-instagram)"
                  value={link.icon}
                  onChange={(e) => updateLink(i, 'icon', e.target.value)}
                />
                <input
                  type="text"
                  className="app-settings__input"
                  placeholder="Label"
                  value={link.label}
                  onChange={(e) => updateLink(i, 'label', e.target.value)}
                />
                <input
                  type="url"
                  className="app-settings__input"
                  placeholder="https://…"
                  value={link.url}
                  onChange={(e) => updateLink(i, 'url', e.target.value)}
                />
                <button
                  type="button"
                  className="event-detail__remove-btn"
                  onClick={() => removeLink(i)}
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </section>
    </form>
  );
};
